import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';

/**
 * The approval queue.
 *
 * Nothing reaches a client without passing through this screen. That is deliberate: these
 * are the operator's relationships, and a tone-deaf automated message costs more than a
 * missed rebook ever would.
 */
export default function Reminders() {
  const drafts = useQuery(api.reminders.listDrafts, {});
  const approve = useMutation(api.reminders.approve);
  const edit = useMutation(api.reminders.edit);
  const cancel = useMutation(api.reminders.cancel);
  const [editing, setEditing] = useState<Record<string, string>>({});

  if (drafts === undefined) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1>Reminders waiting for you</h1>
      <p className="muted">
        Drafted automatically four weeks before work falls due. Nothing sends until you approve it.
      </p>

      {drafts.length === 0 ? (
        <div className="card muted">Nothing waiting. Drafts appear here as work comes due.</div>
      ) : null}

      {drafts.map(({ reminder, client, job }) => {
        const id = reminder._id as Id<'reminders'>;
        const body = editing[id] ?? reminder.draftBody;
        return (
          <div className="card" key={id}>
            <div className="spread">
              <strong>{client?.name ?? 'Client'}</strong>
              <span className="pill">
                {reminder.channel} · {reminder.toAddress}
              </span>
            </div>
            <p className="muted small">
              {job ? `${job.assetCount} items due ${new Date(job.dueAt).toLocaleDateString('en-AU')}` : ''}
            </p>
            <textarea
              value={body}
              onChange={(e) => setEditing((s) => ({ ...s, [id]: e.target.value }))}
            />
            <div className="row" style={{ marginTop: '.6rem' }}>
              <button
                onClick={async () => {
                  if (editing[id] && editing[id] !== reminder.draftBody) {
                    await edit({ reminderId: id, body: editing[id]! });
                  }
                  await approve({ reminderId: id });
                }}
              >
                Approve &amp; send
              </button>
              <button className="quiet" onClick={() => void cancel({ reminderId: id })}>
                Don't send
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}
